import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Video, MapPin, ExternalLink, Clock } from 'lucide-react';
import { format, isToday, isTomorrow, formatDistanceToNow } from 'date-fns';

/**
 * Desktop Upcoming Meetings component for the Dashboard.
 * Shows real Calendly meetings synced via OAuth.
 */
const UpcomingMeetings = ({ meetings, loading, calendlyConnected }) => {
    const navigate = useNavigate();

    if (!calendlyConnected) {
        return (
            <div className="col-span-12 lg:col-span-5 relative">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-base font-medium text-notion-text flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-stone-400" />
                        Upcoming Meetings
                    </h2>
                </div>

                <div className="border border-dashed border-stone-300 rounded-lg p-8 text-center">
                    <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Calendar className="w-6 h-6 text-blue-500" />
                    </div>
                    <h3 className="text-sm font-medium text-notion-text mb-1">Connect Calendly</h3>
                    <p className="text-xs text-stone-400 mb-4">
                        See your upcoming meetings right here on your dashboard.
                    </p>
                    <button
                        onClick={() => navigate('/settings?section=integrations')}
                        className="inline-flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded text-xs font-medium hover:bg-blue-700 transition"
                    >
                        <ExternalLink className="w-3 h-3" />
                        Connect in Settings
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="col-span-12 lg:col-span-5 relative">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-medium text-notion-text flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-stone-400" />
                    Upcoming Meetings
                </h2>
                <span className="text-xs text-notion-text-light">
                    {meetings.length > 0 ? `${meetings.length} upcoming` : ''}
                </span>
            </div>

            {loading ? (
                <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="p-4 rounded-lg border border-notion-border animate-pulse">
                            <div className="h-3 w-32 bg-gray-200 rounded mb-2" />
                            <div className="h-4 w-48 bg-gray-200 rounded mb-1" />
                            <div className="h-3 w-36 bg-gray-200 rounded" />
                        </div>
                    ))}
                </div>
            ) : meetings.length === 0 ? (
                <div className="border border-notion-border rounded-lg p-8 text-center">
                    <p className="text-sm text-notion-text-light">No upcoming meetings</p>
                    <p className="text-xs text-stone-400 mt-1">Your next Calendly meetings will appear here.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {meetings.map((meeting) => (
                        <MeetingCard key={meeting.id} meeting={meeting} />
                    ))}
                </div>
            )}

            {!loading && meetings.length > 0 && (
                <div className="mt-4 text-center">
                    <p className="text-xs text-notion-text-light">
                        Synced from Calendly
                    </p>
                </div>
            )}
        </div>
    );
};

const MeetingCard = ({ meeting }) => {
    const startDate = meeting.startTime instanceof Date ? meeting.startTime : new Date(meeting.startTime);
    const endDate = meeting.endTime instanceof Date ? meeting.endTime : new Date(meeting.endTime);
    const isVideoCall = meeting.location?.includes('http') || meeting.location?.includes('zoom') || meeting.location?.includes('meet');
    const Icon = isVideoCall ? Video : MapPin;

    const formatDayLabel = (date) => {
        if (isToday(date)) return 'Today';
        if (isTomorrow(date)) return 'Tomorrow';
        return format(date, 'EEE, MMM d');
    };

    const timeRange = `${format(startDate, 'h:mm a')} – ${format(endDate, 'h:mm a')}`;
    const dayLabel = formatDayLabel(startDate);
    const relativeTime = formatDistanceToNow(startDate, { addSuffix: true });

    return (
        <div className="group relative p-4 rounded-lg border border-notion-border bg-white transition-all hover:border-stone-300 hover:shadow-sm">
            <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-status-good-text">{dayLabel}</span>
                        <span className="text-[10px] text-stone-400">{relativeTime}</span>
                    </div>
                    <div className="text-sm font-medium text-notion-text truncate">{meeting.name}</div>
                    {(meeting.inviteeName || meeting.inviteeEmail) && (
                        <div className="text-xs text-notion-text-light mt-0.5 truncate">
                            with {meeting.inviteeName || meeting.inviteeEmail}
                        </div>
                    )}
                    <div className="flex items-center gap-1.5 mt-1.5">
                        <Clock className="w-3 h-3 text-stone-400" />
                        <span className="text-[11px] text-stone-500">{timeRange}</span>
                    </div>
                </div>
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-stone-100 flex-shrink-0 ml-3">
                    <Icon className="w-4 h-4 text-stone-500" />
                </div>
            </div>
        </div>
    );
};

export default UpcomingMeetings;
